/**
 * Baumdiagramme für Wahrscheinlichkeiten (Stochastik): Äste mit
 * Wahrscheinlichkeiten, an den Blättern die Pfadwahrscheinlichkeit (Produkt
 * entlang des Pfades) – exakt als Bruch gerechnet, nicht als Kommazahl.
 *
 * Das Diagramm landet als Mermaid-Block in der Notiz. Die Bearbeitungsdaten
 * stehen als Kommentarzeile `%% baum=…` im Block, damit der Editor ihn wieder
 * öffnen kann (wie die Formelzeile bei Tabellen, siehe tableFormulas.ts).
 *
 * Ohne Importe: `node src/features/editor/treeDiagram.check.ts` prüft die Datei.
 */

export interface TreeNode {
  /** Name des Ereignisses (am Knoten). */
  name: string;
  /** Wahrscheinlichkeit des Astes zu diesem Knoten, wie getippt (`1/3`, `0,25`, `25%`); leer = Gegenwahrscheinlichkeit. */
  p: string;
  children: TreeNode[];
}

export interface TreeData {
  /** An den Blättern „P = …“ anzeigen. */
  showPaths: boolean;
  root: TreeNode;
}

export const TREE_MARK = "%% baum=";

// ---------------------------------------------------------------------------
// Brüche (BigInt: ein Pfad über viele Stufen überläuft sonst schnell)
// ---------------------------------------------------------------------------

export interface Rat {
  n: bigint;
  d: bigint;
}

const abs = (a: bigint) => (a < 0n ? -a : a);
function gcd(a: bigint, b: bigint): bigint {
  a = abs(a);
  b = abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1n;
}
export function rat(n: bigint, d: bigint = 1n): Rat {
  if (d < 0n) [n, d] = [-n, -d];
  const g = gcd(n, d);
  return { n: n / g, d: d / g };
}
const add = (a: Rat, b: Rat) => rat(a.n * b.d + b.n * a.d, a.d * b.d);
const mul = (a: Rat, b: Rat) => rat(a.n * b.n, a.d * b.d);
const eq = (a: Rat, b: Rat) => a.n === b.n && a.d === b.d;
const ONE = rat(1n);
const ZERO = rat(0n);

export function showRat(r: Rat): string {
  return r.d === 1n ? String(r.n) : `${r.n}/${r.d}`;
}

/** `0,25` / `3` -> Bruch; sonst null. */
function decimalRat(t: string): Rat | null {
  const m = /^(\d+)(?:[.,](\d+))?$/.exec(t.trim());
  if (!m) return null;
  const frac = m[2] ?? "";
  return rat(BigInt(m[1] + frac), 10n ** BigInt(frac.length));
}

/**
 * Wahrscheinlichkeit aus Text: `1/3`, `2 / 6`, `0,25`, `0.25`, `25%`,
 * `\frac{1}{3}`. Nur Werte von 0 bis 1.
 */
export function parseProb(text: string): Rat | null {
  let t = text.trim().replace(/\$/g, "");
  if (!t) return null;
  const frac = /^\\d?frac\{([^{}]+)\}\{([^{}]+)\}$/.exec(t);
  if (frac) t = `${frac[1]}/${frac[2]}`;
  let r: Rat | null = null;
  if (t.endsWith("%")) {
    const v = decimalRat(t.slice(0, -1));
    r = v && rat(v.n, v.d * 100n);
  } else if (t.includes("/")) {
    const [a, b, ...rest] = t.split("/");
    const x = decimalRat(a);
    const y = decimalRat(b ?? "");
    r = rest.length || !x || !y || y.n === 0n ? null : rat(x.n * y.d, x.d * y.n);
  } else r = decimalRat(t);
  return r && r.n >= 0n && r.n <= r.d ? r : null;
}

// ---------------------------------------------------------------------------
// Auswertung
// ---------------------------------------------------------------------------

export interface Evaluation {
  /** Wahrscheinlichkeit des Astes zu jedem Knoten (Gegenwahrscheinlichkeit ergänzt). */
  edge: Map<TreeNode, Rat | null>;
  /** Pfadwahrscheinlichkeit zu jedem Knoten (Wurzel = 1). */
  path: Map<TreeNode, Rat | null>;
  /** Meldungen für den Editor („A: Summe der Äste 5/6 statt 1“). */
  issues: string[];
  /** Summe aller Blätter – 1, wenn der Baum stimmt. */
  leafSum: Rat | null;
}

export function evaluate(root: TreeNode): Evaluation {
  const edge = new Map<TreeNode, Rat | null>();
  const path = new Map<TreeNode, Rat | null>();
  const issues: string[] = [];
  let leafSum: Rat | null = ZERO;

  const walk = (node: TreeNode, p: Rat | null) => {
    path.set(node, p);
    if (!node.children.length) {
      leafSum = leafSum && p ? add(leafSum, p) : null;
      return;
    }
    const parsed = node.children.map((c) => (c.p.trim() ? parseProb(c.p) : undefined));
    const empty = parsed.filter((x) => x === undefined).length;
    const bad = parsed.some((x) => x === null);
    const where = node.name.trim() || "Start";
    if (bad) issues.push(`${where}: eine Wahrscheinlichkeit ist ungültig (z. B. 1/3, 0,25 oder 25 %).`);
    let known = ZERO;
    for (const x of parsed) if (x) known = add(known, x);
    // Genau ein Ast ohne Angabe: bekommt, was zu 1 fehlt.
    let rest: Rat | null = null;
    if (empty === 1 && !bad) {
      rest = add(ONE, mul(known, rat(-1n)));
      if (rest.n < 0n) {
        issues.push(`${where}: die Äste ergeben schon ${showRat(known)} – mehr als 1.`);
        rest = null;
      }
    } else if (empty > 1) issues.push(`${where}: bei höchstens einem Ast darf die Wahrscheinlichkeit fehlen.`);
    else if (!bad && !eq(known, ONE)) issues.push(`${where}: die Äste ergeben ${showRat(known)} statt 1.`);

    node.children.forEach((c, i) => {
      const e = parsed[i] === undefined ? rest : (parsed[i] as Rat | null);
      edge.set(c, e);
      walk(c, p && e ? mul(p, e) : null);
    });
  };
  walk(root, ONE);
  return { edge, path, issues, leafSum };
}

// ---------------------------------------------------------------------------
// Mermaid
// ---------------------------------------------------------------------------

/** Beschriftung ohne Zeichen, die Mermaid oder unsere Vorverarbeitung aus dem Tritt bringen. */
function clean(t: string): string {
  return t.replace(/\$/g, "").replace(/["\n#]/g, "'").replace(/\|/g, "¦").replace(/[{}[\]]/g, "").trim();
}

/** Astbeschriftung: wie getippt, wenn es schon eine schlichte Zahl ist (2/4 bleibt 2/4), sonst als Bruch. */
function edgeLabel(typed: string, value: Rat | null): string {
  const t = typed.trim().replace(/\s*\/\s*/g, "/");
  if (/^(\d+\/\d+|\d+(?:[.,]\d+)?%?)$/.test(t)) return t;
  return value ? showRat(value) : clean(typed);
}

export function treeCode(data: TreeData, ev: Evaluation = evaluate(data.root)): string {
  const lines = ["flowchart LR"];
  let id = 0;
  const idOf = new Map<TreeNode, string>();
  const nameOf = (n: TreeNode) => clean(n.name) || "–";

  const declare = (node: TreeNode) => {
    const nid = `n${id++}`;
    idOf.set(node, nid);
    const leaf = !node.children.length;
    if (node === data.root) lines.push(`  ${nid}(("${nameOf(node)}"))`);
    else {
      const p = ev.path.get(node);
      const extra = leaf && data.showPaths && p ? `<br/>P = ${showRat(p)}` : "";
      lines.push(`  ${nid}["${nameOf(node)}${extra}"]`);
    }
    node.children.forEach(declare);
  };
  declare(data.root);

  const link = (node: TreeNode) => {
    for (const c of node.children) {
      lines.push(`  ${idOf.get(node)} -->|"${edgeLabel(c.p, ev.edge.get(c) ?? null)}"| ${idOf.get(c)}`);
      link(c);
    }
  };
  link(data.root);
  return lines.join("\n");
}

// ---------------------------------------------------------------------------
// Ablegen im Dokument
// ---------------------------------------------------------------------------

interface Packed {
  n: string;
  p?: string;
  c?: Packed[];
}

const pack = (n: TreeNode, root = false): Packed => ({
  n: n.name,
  ...(root || !n.p ? {} : { p: n.p }),
  ...(n.children.length ? { c: n.children.map((c) => pack(c)) } : {}),
});
const unpack = (x: Packed): TreeNode => ({
  name: String(x.n ?? ""),
  p: String(x.p ?? ""),
  children: (x.c ?? []).map(unpack),
});

/** Fertiger Markdown-Block samt Kommentarzeile mit den Bearbeitungsdaten. */
export function treeBlock(data: TreeData): string {
  const enc = encodeURIComponent(JSON.stringify({ s: data.showPaths ? 1 : 0, r: pack(data.root, true) }));
  return "```mermaid\n" + treeCode(data) + "\n" + TREE_MARK + enc + "\n```";
}

function decode(enc: string): TreeData | null {
  try {
    const o = JSON.parse(decodeURIComponent(enc)) as { s?: number; r?: Packed };
    if (!o.r || typeof o.r !== "object") return null;
    return { showPaths: o.s !== 0, root: unpack(o.r) };
  } catch {
    return null;
  }
}

/**
 * Das Baumdiagramm, in dessen Mermaid-Block `pos` liegt (sonst null) – samt
 * Zeichenbereich des ganzen Blocks, damit der Editor ihn ersetzen kann.
 */
export function findTreeAt(text: string, pos: number): { start: number; end: number; data: TreeData } | null {
  const lines = text.split("\n");
  let acc = 0;
  let at = 0;
  for (let i = 0; i < lines.length; i++) {
    if (pos <= acc + lines[i].length) {
      at = i;
      break;
    }
    acc += lines[i].length + 1;
  }
  let open = -1;
  for (let i = at; i >= 0 && at - i < 400; i--) {
    if (/^```mermaid\s*$/.test(lines[i])) {
      open = i;
      break;
    }
    if (i < at && /^```/.test(lines[i])) return null; // der Zaun davor gehört zu einem anderen Block
  }
  if (open < 0) return null;
  let close = -1;
  for (let i = open + 1; i < lines.length && i - open < 400; i++) {
    if (/^```\s*$/.test(lines[i])) {
      close = i;
      break;
    }
  }
  if (close < 0 || at > close) return null;
  const mark = lines.slice(open + 1, close).find((l) => l.startsWith(TREE_MARK));
  const data = mark ? decode(mark.slice(TREE_MARK.length).trim()) : null;
  if (!data) return null;
  const start = lines.slice(0, open).reduce((n, l) => n + l.length + 1, 0);
  const end = lines.slice(0, close + 1).reduce((n, l) => n + l.length + 1, 0) - 1;
  return { start, end, data };
}

// ---------------------------------------------------------------------------
// Schnellstart: Urne und gleichverteilte Ergebnisse
// ---------------------------------------------------------------------------

/** So viele Blätter höchstens – ein größerer Baum ist nicht mehr lesbar. */
export const MAX_LEAVES = 200;

function checkSize(branches: number, depth: number): void {
  if (branches ** depth > MAX_LEAVES) {
    throw new Error(`Das wären über ${MAX_LEAVES} Pfade – bitte weniger Stufen oder Ergebnisse.`);
  }
}

/**
 * Ziehen aus einer Urne: je Farbe `Anzahl/Gesamt` am Ast (ungekürzt, wie man
 * es in der Schule aufschreibt). Ohne Zurücklegen sinken Anzahl und Gesamt.
 */
export function urnTree(colors: { name: string; count: number }[], draws: number, replace: boolean): TreeNode {
  const used = colors.filter((c) => c.name.trim() && c.count > 0);
  if (!used.length) throw new Error("Mindestens eine Farbe mit Anzahl größer 0.");
  const total = used.reduce((s, c) => s + c.count, 0);
  if (!replace && draws > total) throw new Error(`Es sind nur ${total} Kugeln in der Urne.`);
  checkSize(used.length, draws);
  const grow = (counts: number[], depth: number): TreeNode[] => {
    if (depth === draws) return [];
    const sum = counts.reduce((s, c) => s + c, 0);
    return used.flatMap((c, i) => {
      if (counts[i] === 0) return [];
      const next = replace ? counts : counts.map((x, j) => (j === i ? x - 1 : x));
      return [{ name: c.name.trim(), p: `${counts[i]}/${sum}`, children: grow(next, depth + 1) }];
    });
  };
  return { name: "Start", p: "", children: grow(used.map((c) => c.count), 0) };
}

/** Alle Ergebnisse gleich wahrscheinlich (Münze, Würfel …), mehrere Stufen. */
export function uniformTree(outcomes: string[], stages: number): TreeNode {
  const names = outcomes.map((o) => o.trim()).filter(Boolean);
  if (names.length < 2) throw new Error("Mindestens zwei Ergebnisse, durch Komma getrennt.");
  checkSize(names.length, stages);
  const grow = (depth: number): TreeNode[] =>
    depth === stages ? [] : names.map((name) => ({ name, p: `1/${names.length}`, children: grow(depth + 1) }));
  return { name: "Start", p: "", children: grow(0) };
}

/** „rot 3, blau 2“ oder „rot: 3; blau: 2“ -> Farben mit Anzahl. */
export function parseColors(text: string): { name: string; count: number }[] {
  return text
    .split(/[,;\n]+/)
    .map((part) => /^\s*(.+?)\s*[:=]?\s+(\d+)\s*$|^\s*(.+?)\s*[:=]\s*(\d+)\s*$/.exec(part))
    .filter((m): m is RegExpExecArray => !!m)
    .map((m) => ({ name: (m[1] ?? m[3]).trim(), count: Number(m[2] ?? m[4]) }));
}
